import { ReactElement, ReactNode } from 'react'
import { cloneDeep } from 'lodash-es'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import type { FilterViewData, FilterViewModel, FilterViewsData } from '@queries'
import { validate } from '../middleware'

interface Props {
  children: ReactNode
}

interface FilterViewStoreInitialState {
  savedFilterViews: FilterViewModel[]
  activeFilterViewId: string | null
}

interface FilterViewStoreType extends FilterViewStoreInitialState {
  setFromServer: (data: FilterViewsData) => void
  getFilterView: (id: string) => FilterViewModel | null
  getDefaultFilterView: () => FilterViewModel | null
  setActiveFilterViewId: (id: string | null) => void
  clear: () => void
}

const initialState: FilterViewStoreInitialState = {
  savedFilterViews: [],
  activeFilterViewId: null,
}

const createStore = () =>
  create<FilterViewStoreType>(
    validate(
      combine(cloneDeep(initialState), (set, get) => ({
        clear: () => {
          set(cloneDeep(initialState))
        },

        setFromServer: (data: FilterViewsData) => {
          set({
            savedFilterViews: data.filterViews,
            activeFilterViewId: data.activeFilterViewId,
          })
        },

        getFilterView: (id: string) => {
          return get().savedFilterViews.find(view => view.id === id) || null
        },

        getDefaultFilterView: () => {
          return get().savedFilterViews.find(view => view.isDefault) || null
        },

        setActiveFilterViewId: (id: string | null) => {
          set({ activeFilterViewId: id })
        },
      })),
    ),
  )

const useFilterViewStore = createStore()

const FilterViewStoreProvider = ({ children }: Props): ReactElement => <>{children}</>

export { FilterViewStoreProvider, useFilterViewStore }
export type { FilterViewModel, FilterViewData }
